import {
  DataGrid,
  DataGridActions,
  DataGridColumns,
  DataGridSections,
  DataGridSelectedActions,
} from '@components/DataGrid/DataGrid'
import { DataGridPerson } from '@components/DataGrid/DataGridPerson'
import ExampleUser from '@assets/exampleUser.png'
import { useNavigate } from 'react-router-dom'
import { RoutePaths } from '@interfaces/routePaths'
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Button,
  Group,
  Menu,
  ThemeIcon,
  Tooltip,
} from '@mantine/core'
import { randomArrayItem } from '@utils/randomArrayItem'
import { StageListItem } from '@components/StageList/StageList'
import { DataGridList } from '@components/DataGrid/DataGridList'
import {
  IconCalendarEvent,
  IconChecklist,
  IconChevronDown,
  IconDots,
} from '@tabler/icons-react'
import { randomBool } from '@utils/randomBool'

export interface RecordsProps {
  handleNavigate: () => void
}

const MOCK_DATA = Array(30)
  .fill(0)
  .map((_, i) => {
    return {
      id: `records-${i}`,
      persona: randomArrayItem([
        'Contact',
        'Staff',
        'Child',
        'Child',
        'Record person',
        'Partner',
      ]),
      stage: (
        <Badge variant="outline" color="gray" style={{ userSelect: 'none' }}>
          {
            randomArrayItem([
              'Cold',
              'Engage',
              'Qualify',
              'Pitch',
              'Manage issues',
              'Close',
              'Nurture',
            ]) as string
          }
        </Badge>
      ),
      tags: <DataGridList title="Tags" data={['Some', 'Another', 'Another']} />,
      owner: (
        <Tooltip label="Alfredo Carrillo">
          <Avatar size="sm" variant="filled" color="dark.3">
            AC
          </Avatar>
        </Tooltip>
      ),
      status: (
        <Group gap={0} wrap="nowrap">
          {randomBool() ? (
            <Tooltip label="1 upcoming meeting">
              <ThemeIcon variant="transparent" color="dark.7">
                <IconCalendarEvent size={22} />
              </ThemeIcon>
            </Tooltip>
          ) : (
            <ThemeIcon variant="transparent" color="gray.5">
              <IconCalendarEvent size={22} />
            </ThemeIcon>
          )}
          {randomBool() ? (
            <Tooltip label="1 due task">
              <ThemeIcon variant="transparent" color="dark.7">
                <IconChecklist size={22} />
              </ThemeIcon>
            </Tooltip>
          ) : (
            <ThemeIcon variant="transparent" color="gray.5">
              <IconChecklist size={22} />
            </ThemeIcon>
          )}
        </Group>
      ),
      phone: (
        <Anchor href="tel:+1 (212) 555-1212" size="sm" c="dark.7">
          +1 (212) 555-1212
        </Anchor>
      ),
      email: (
        <Anchor href="mailto:alfredo@carrillo.com" size="sm" c="dark.7">
          alfredo@mybluecard.org
        </Anchor>
      ),
      lastUpdated: randomArrayItem(['Today', '6w 3d', '2mo 21d', '1yr 3mo']),
    }
  })

export const Records = (props: RecordsProps) => {
  const { handleNavigate } = props

  const sections: DataGridSections[] = [
    {
      label: 'All Records',
      href: '#',
      default: true,
    },
    {
      label: 'Adults',
      href: '#',
    },
    {
      label: 'Children',
      href: '#',
    },
    {
      label: 'Contacts',
      href: '#',
    },
    {
      label: 'Record Persons',
      href: '#',
    },
    {
      label: 'Staff',
      href: '#',
    },
    {
      label: 'Partners',
      href: '#',
    },
  ]

  const columns: DataGridColumns[] = [
    {
      label: 'Name',
      prop: 'name',
      defaultSort: true,
      pinned: true,
    },
    {
      label: 'Persona',
      prop: 'persona',
    },
    {
      label: 'Stage',
      prop: 'stage',
    },
    {
      label: 'Tags',
      prop: 'tags',
    },
    {
      label: 'Phone',
      prop: 'phone',
    },
    {
      label: 'Email',
      prop: 'email',
    },
    {
      label: 'Status',
      prop: 'status',
    },
    {
      label: 'Owner',
      prop: 'owner',
    },
    {
      label: 'Last updated',
      prop: 'lastUpdated',
    },
  ]

  const actions: DataGridActions[] = [
    {
      label: 'Request edit access',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Request data share',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Request data check',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Request callback',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Edit custom fields',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Add tags',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Remove tag',
      color: 'red',
      onClick: (item: any) => console.log('Action'),
    },
    {
      label: 'Delete record',
      color: 'red',
      onClick: (item: any) => console.log('Action'),
    },
  ]

  const selectedActions: DataGridSelectedActions[] = [
    {
      label: 'Edit',
      onClick: (items: any) => console.log('Selected Actions: Edit'),
    },
    {
      label: 'Tags',
      onClick: (items: any) => console.log('Selected Actions: Tags'),
      component: (
        <div>
          <Menu position="bottom-start">
            <Menu.Target>
              <Button
                variant="default"
                rightSection={<IconChevronDown size={18} />}
              >
                Tags
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>Add tags</Menu.Item>
              <Menu.Item color="red">Remove tags</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
    {
      label: 'Select all linked records',
      onClick: (items: any) =>
        console.log('Selected Actions: Select all linked records'),
    },
    {
      label: 'Options',
      onClick: (items: any) => {},
      component: (
        <div>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="default" radius="xl" aria-label="Options">
                <IconDots
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>Request edit access</Menu.Item>
              <Menu.Item>Request data share</Menu.Item>
              <Menu.Item>Request data check</Menu.Item>
              <Menu.Item>Request callback</Menu.Item>
              <Menu.Item>Link records</Menu.Item>
              <Menu.Item>Create email list</Menu.Item>
              <Menu.Item color="red">Delete records</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
  ]

  const stages: StageListItem[] = [
    {
      name: 'Total',
      count: 5487,
    },
    {
      name: 'Cold',
      count: 1625,
    },
    {
      name: 'Engage',
      count: 879,
    },
    {
      name: 'Qualify',
      count: 1741,
    },
    {
      name: 'Pitch',
      count: 545,
    },
    {
      name: 'Manage issues',
      count: 697,
    },
    {
      name: 'Close',
      count: 121,
    },
    {
      name: 'Nurture',
      count: 57,
    },
    {
      name: 'No stage',
      count: 11,
    },
  ]

  const data = MOCK_DATA.map((curr, i) => {
    return {
      ...curr,
      name: (
        <DataGridPerson
          name="Alfredo Carrillo"
          image={ExampleUser}
          status="online"
          onClick={handleNavigate}
        />
      ),
    }
  })

  return (
    <>
      <DataGrid
        data={data}
        columns={columns}
        sections={sections}
        actions={actions}
        results={data.length}
        stages={stages}
        selectedActions={selectedActions}
        page={1}
      />
    </>
  )
}
